import React from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox } from "primereact/checkbox";
import { ProgressSpinner } from "primereact/progressspinner";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import { handleApiResponse } from 'utilities/apiUtils';
import '../../../styles/pages.css';
import ErrorState from 'components/ErrorState';
import { formatUsPhone } from 'utilities/formatPhone';

export type ViewCOIRequestProps = {
  visible: boolean;
  onHide: () => void;
  databaseId: string | null;
  showSendCertificateButton?: boolean;
};

const labelStyle = { fontWeight: 500, color: "#0057A4", marginBottom: 2 };
const sectionTitleStyle = {
  fontWeight: 600,
  fontSize: 15,
  color: "#ba170b", // Farmers red
  margin: "0 0 10px 0",
  letterSpacing: 0.2,
};
const sectionStyle = {
  marginBottom: 18,
  paddingBottom: 10,
  borderBottom: "1px solid #A7B5D3",
};

const API_URL = `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/view-coi-request`;

const ViewCOIRequest: React.FC<ViewCOIRequestProps> = ({
  visible,
  onHide,
  databaseId,
  showSendCertificateButton,
}) => {
  const navigate = useNavigate();
  const [data, setData] = React.useState<any | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!visible || !databaseId) return;
    setLoading(true);
    setError(null);
    setData(null);
    const NCToken = localStorage.getItem("NCToken") || "";
    const IGToken = localStorage.getItem("igtoken") || "";
    fetch(`${API_URL}?databaseId=${databaseId}`, {
      headers: {
        NCToken: NCToken,
        Authorization: IGToken ? `Bearer ${IGToken}` : "",
        Accept: "application/json",
      },
    })
      .then(res => handleApiResponse(res, navigate))
      .then(res => res.json())
      .then((res) => {
        if (res && Array.isArray(res.value) && res.value.length > 0) {
          setData(res.value[0]);
        } else {
          setError("No data found.");
        }
      })
      .catch(() => setError("Failed to fetch request details."))
      .finally(() => setLoading(false));
  }, [visible, databaseId]);

  const FullWidthRow: React.FC<{
    label: string;
    value: string | undefined;
  }> = ({ label, value }) => (
    <div style={{ marginBottom: 10 }}>
      <div style={labelStyle}>{label}</div>
      <InputTextarea
        value={value || ""}
        readOnly
        rows={2}
        className="p-inputtext-sm"
        style={{ width: "100%" }}
      />
    </div>
  );

  const renderHeader = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: showSendCertificateButton === false ? "flex-start" : "space-between" }}>
      <span style={{ color: '#ba170b', fontWeight: 600 }}>View COI Request</span>
      {showSendCertificateButton !== false && (
        <Button
          label="Send Certificate"
          icon="pi pi-send"
          className="p-button-sm appButton--primary"
          style={{ marginRight: 16 }}
          onClick={() => navigate('/sendcertificate', { state: { coiRequestData: data } })}
        />
      )}
    </div>
  );

  return (
    <Dialog
      header={renderHeader}
      visible={visible}
      style={{ width: 800, maxWidth: "98vw" }}
      onHide={onHide}
      modal
      className="pageCard"
    >
      {loading ? (
        <div style={{ minHeight: 180, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <ProgressSpinner style={{ width: 35, height: 35 }} strokeWidth="3" />
        </div>
      ) : error ? (
        <div style={{ minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ErrorState /></div>
      ) : data ? (
        <form className="page" style={{ gap: 0 }}>
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>Holder information</div>
            <div
              className="page__twoCol"
              style={{ gridTemplateColumns: "1fr 1fr", gap: 18 }}
            >
              <div>
                <div style={labelStyle}>Holder name</div>
                <InputText
                  value={data.holderName || ""}
                  readOnly
                  className="p-inputtext-sm"
                  style={{ width: "100%", marginBottom: 10 }}
                />
                <div style={labelStyle}>Holder address</div>
                <InputText
                  value={data.holderAddress || ""}
                  readOnly
                  className="p-inputtext-sm"
                  style={{ width: "100%", marginBottom: 10 }}
                />
                <div style={labelStyle}>City</div>
                <InputText
                  value={data.holderCity || ""}
                  readOnly
                  className="p-inputtext-sm"
                  style={{ width: "100%", marginBottom: 10 }}
                />
              </div>
              <div>
                <div style={labelStyle}>State</div>
                <InputText
                  value={data.holderState || ""}
                  readOnly
                  className="p-inputtext-sm"
                  style={{ width: "100%", marginBottom: 10 }}
                />
                <div style={labelStyle}>Zip</div>
                <InputText
                  value={data.holderZip || ""}
                  readOnly
                  className="p-inputtext-sm"
                  style={{ width: "100%", marginBottom: 10 }}
                />
              </div>
            </div>
          </div>
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>Policy & request information</div>
            <div
              className="page__twoCol"
              style={{ gridTemplateColumns: "1fr 1fr", gap: 18 }}
            >
              <div>
                <div style={labelStyle}>Policy LOB</div>
                <InputText
                  value={(data.policyLOB || "").replace(/\bWorker's Compensation\b/g, "Workers' Compensation")}
                  readOnly
                  className="p-inputtext-sm"
                  style={{ width: "100%", marginBottom: 10 }}
                />
                <div style={labelStyle}>Policy number</div>
                <InputText
                  value={data.policyNumber || ""}
                  readOnly
                  className="p-inputtext-sm"
                  style={{ width: "100%", marginBottom: 10 }}
                />
                <div style={labelStyle}>Stage</div>
                <InputText
                  value={data.stage || ""}
                  readOnly
                  className="p-inputtext-sm"
                  style={{ width: "100%", marginBottom: 10 }}
                />
                <div style={labelStyle}>Custom type</div>
                <InputText
                  value={data.customType || ""}
                  readOnly
                  className="p-inputtext-sm"
                  style={{ width: "100%", marginBottom: 10 }}
                />
                <div style={labelStyle}>Agents</div>
                <InputText
                  value={
                    Array.isArray(data.agents)
                      ? data.agents
                          .map((a: any) => `${a.firstName} ${a.lastName}`)
                          .join(", ")
                      : ""
                  }
                  readOnly
                  className="p-inputtext-sm"
                  style={{ width: "100%", marginBottom: 10 }}
                />
                <div style={labelStyle}>Policies</div>
                <InputText
                  value={
                    Array.isArray(data.policies)
                      ? data.policies
                          .map((p: any) => p.number || p.databaseId)
                          .join(", ")
                      : ""
                  }
                  readOnly
                  className="p-inputtext-sm"
                  style={{ width: "100%", marginBottom: 10 }}
                />
              </div>
              <div>
                <div style={labelStyle}>Status</div>
                <InputText
                  value={data.status || ""}
                  readOnly
                  className="p-inputtext-sm"
                  style={{ width: "100%", marginBottom: 10 }}
                />
                <div style={labelStyle}>Due date</div>
                <InputText
                  value={data.dueDate ? String(data.dueDate).split("T")[0] : ""}
                  readOnly
                  className="p-inputtext-sm"
                  style={{ width: "100%", marginBottom: 10 }}
                />
                <div style={labelStyle}>Additional interest requested</div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 10,
                  }}
                >
                  <Checkbox
                    checked={!!data.additionalInterestRequested}
                    readOnly
                    inputId="addlInterest"
                    style={{ marginRight: 8 }}
                  />
                  <label htmlFor="addlInterest" className="page__muted">
                    {data.additionalInterestRequested ? "Yes" : "No"}
                  </label>
                </div>
                <div style={labelStyle}>Waiver subrogation requested</div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 10,
                  }}
                >
                  <Checkbox
                    checked={!!data.waverSubrogationrequested}
                    readOnly
                    inputId="waverSubr"
                    style={{ marginRight: 8 }}
                  />
                  <label htmlFor="waverSubr" className="page__muted">
                    {data.waverSubrogationrequested ? "Yes" : "No"}
                  </label>
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 18, marginTop: 0 }}>
              <div style={{ flex: 1 }}>
                <FullWidthRow
                  label="Additional requirements"
                  value={data.additionalrequirements}
                />
              </div>
              <div style={{ flex: 1 }}>
                <FullWidthRow label="Description" value={data.description} />
              </div>
            </div>
          </div>
          <div>
            <div style={sectionTitleStyle}>Insured information</div>
            <div
              className="page__twoCol"
              style={{ gridTemplateColumns: "1fr 1fr", gap: 18 }}
            >
              <div>
                <div style={labelStyle}>Insured name</div>
                <InputText
                  value={data.insuredName || ""}
                  readOnly
                  className="p-inputtext-sm"
                  style={{ width: "100%", marginBottom: 10 }}
                />
              </div>
              <div>
                <div style={labelStyle}>Insured email</div>
                <InputText
                  value={data.insuredEmail || ""}
                  readOnly
                  className="p-inputtext-sm"
                  style={{ width: "100%", marginBottom: 10 }}
                />
                <div style={labelStyle}>Insured phone</div>
                <InputText
                  value={formatUsPhone(data.insuredPhone) || ""}
                  readOnly
                  className="p-inputtext-sm"
                  style={{ width: "100%", marginBottom: 10 }}
                />
              </div>
            </div>
          </div>
        </form>
      ) : null}
    </Dialog>
  );
};

export default ViewCOIRequest;
